import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    EmptyState, ErrorState, LoadingState, Tooltip,
} from '../ui';
import {
    PRESETS, aggregateVariantRow, filterRiskVariants, formatSignalValue, quarterLabels,
    riskKpis, severityTone, togglePreset,
} from '../../utils/categoryTrends';
import { Sparkline } from './charts';
import { Delta, KpiTile } from './bits';
import { criticalLabel, criticalTip, daysInStockSeverity } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';

const SEVERITY_TEXT = { red: 'High risk', amber: 'Moderate risk', sage: 'Low risk' };

/** Market signals (P-BR-127): backend values and YoY / QoQ, today's formats. */
function MarketSignals({ trendsData }) {
    const { q, yr, prevQ, prevQYear } = quarterLabels(trendsData);
    const signals = trendsData.market_signals || [];
    return (
        <section className="v2-board br2-board" aria-labelledby="br2-ms-h">
            <div className="br2-card-h">
                <h2 id="br2-ms-h">Market Signals</h2>
                <span className="br2-sub">Is the overall market down, or just this category?</span>
            </div>
            {signals.length === 0 ? (
                <p className="br2-bav-empty">No market signal data available.</p>
            ) : (
                <div className="v2-tscroll br2-tscroll">
                    <table className="v2-table br2-sig" aria-label="Market signals">
                        <thead>
                            <tr>
                                <th scope="col"><span>Signal</span></th>
                                <th scope="col" className="v2-r"><span>Current</span></th>
                                <th scope="col" className="v2-r"><span>Q{q} {yr}</span></th>
                                <th scope="col" className="v2-r"><span>Q{q} {yr - 1}</span></th>
                                <th scope="col" className="v2-r"><span>YoY</span></th>
                                <th scope="col" className="v2-r"><span>Q{prevQ} {prevQYear}</span></th>
                                <th scope="col" className="v2-r"><span>QoQ</span></th>
                                <th scope="col" className="v2-r"><span>12m Trend</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {signals.map((sig) => {
                                const spark = (trendsData.market_context || []).map((m) => m[sig.key]).filter((v) => v != null);
                                return (
                                    <tr key={sig.key} data-signal={sig.key}>
                                        <td><b className="br2-strong">{sig.label}</b></td>
                                        <td className="v2-r v2-mono">{formatSignalValue(sig.key, sig.current)}</td>
                                        <td className="v2-r v2-mono">{formatSignalValue(sig.key, sig.current_q_avg)}</td>
                                        <td className="v2-r br2-ink-muted">{formatSignalValue(sig.key, sig.last_year_q_avg)}</td>
                                        <td className="v2-r"><Delta value={sig.yoy_pct} /></td>
                                        <td className="v2-r br2-ink-muted">{formatSignalValue(sig.key, sig.prev_q_avg)}</td>
                                        <td className="v2-r"><Delta value={sig.qoq_pct} /></td>
                                        <td className="v2-r"><Sparkline data={spark} /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

MarketSignals.propTypes = { trendsData: PropTypes.object.isRequired };

/** Risk KPI strip (P-BR-128): today's labels and tooltips. */
function RiskKpis({ variants }) {
    const { criticalDays } = useDealershipSettings();
    const k = riskKpis(variants);
    if (!k) return null;
    return (
        <section className="br2-ctk" aria-label="Category risk figures">
            <KpiTile label="Variants" tip="Distinct equipment variants in current active inventory" value={k.totalVariants} />
            <KpiTile label="Units in Stock" tip="Total active units across all variants and categories" value={k.totalUnits.toLocaleString('en-US')} />
            <KpiTile label="Declining YoY" tip="Categories where YoY sold count dropped more than 5%" value={k.declining} valueClass="br2-ink-crit" />
            <KpiTile label="Growing YoY" tip="Categories where sold count grew more than 5% vs same quarter last year" value={k.growing} valueClass="br2-ink-good" />
            <KpiTile label={criticalLabel(criticalDays)} tip={criticalTip(criticalDays)} value={k.totalCritical} valueClass="br2-ink-crit" />
            {k.worst && (
                <KpiTile
                    testId="kpi-worst"
                    label={`Worst: ${k.worst.variant}`}
                    tip="Variant with the largest year-over-year sold decline"
                    value={`${Math.round(k.worst.yoy_pct)}%`}
                    valueClass="br2-ink-crit"
                />
            )}
        </section>
    );
}

RiskKpis.propTypes = { variants: PropTypes.arrayOf(PropTypes.object).isRequired };

// Past the last aging boundary red, past the critical days amber.
const avgDaysTone = (d, aging) => ({ crit: 'crit', warn: 'warn' }[daysInStockSeverity(d, aging)] || null);

/**
 * Category risk (P-BR-130..138): presets (multi-select, All exclusive) and
 * YoY / QoQ ranges filter at sub-category level; while filtered the count
 * "n of m variants" shows and matching variants open by themselves.
 */
function CategoryRisk({ trendsData }) {
    const { aging } = useDealershipSettings();
    const [presets, setPresets] = useState(() => new Set([0]));
    const [custom, setCustom] = useState({ yoyMin: '', yoyMax: '', qoqMin: '', qoqMax: '' });
    const [expanded, setExpanded] = useState(() => new Set());
    const all = trendsData.variant_trends || [];
    const { q, yr, prevQ, prevQYear } = quarterLabels(trendsData);
    const { variants, hasFilter, subsFor } = filterRiskVariants(all, presets, custom);
    const open = hasFilter ? new Set(variants.map((v) => v.variant)) : expanded;
    const toggle = (name) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });
    const clearAll = () => {
        setPresets(new Set([0]));
        setCustom({ yoyMin: '', yoyMax: '', qoqMin: '', qoqMax: '' });
    };
    const range = (key, label) => (
        <input
            type="number"
            className="v2-input br2-rng-in"
            placeholder={key.endsWith('Min') ? 'Min' : 'Max'}
            aria-label={label}
            value={custom[key]}
            onChange={(e) => setCustom((c) => ({ ...c, [key]: e.target.value }))}
        />
    );
    const head = (label, tip, r = true) => (
        <th scope="col" className={r ? 'v2-r' : undefined}>
            <span className="v2-th-split">
                <span className="br2-th-plain">{label}</span>
                {tip && <Tooltip label={`About ${label}`} content={tip} />}
            </span>
        </th>
    );

    return (
        <section className="v2-board br2-board" aria-labelledby="br2-risk-h">
            <div className="br2-ctl">
                <div className="br2-row">
                    <h2 id="br2-risk-h">Category risk</h2>
                    <span className="br2-sub">Variant, then category. Units sold Q{q} {yr}.</span>
                </div>
                <div className="br2-row">
                    <div className="br2-presets" role="group" aria-label="Quick filters">
                        {PRESETS.map((p, i) => (
                            <Tooltip key={p.label} content={p.tip}>
                                <button
                                    type="button"
                                    className="br2-vchip"
                                    aria-pressed={presets.has(i)}
                                    onClick={() => setPresets((prev) => togglePreset(prev, i))}
                                >
                                    {p.label}
                                </button>
                            </Tooltip>
                        ))}
                    </div>
                    <span className="br2-spacer" />
                    <span className="br2-rng">YoY % {range('yoyMin', 'YoY % minimum')} – {range('yoyMax', 'YoY % maximum')}</span>
                    <span className="br2-rng">QoQ % {range('qoqMin', 'QoQ % minimum')} – {range('qoqMax', 'QoQ % maximum')}</span>
                    {hasFilter && (
                        <span className="br2-rng-meta" data-testid="variant-count">
                            {variants.length} of {all.length} variants
                            <button type="button" className="v2-clear" onClick={clearAll}>Clear all</button>
                        </span>
                    )}
                </div>
            </div>
            <div className="v2-tscroll br2-tscroll">
                <table className="v2-table br2-risk" aria-label="Category risk">
                    <thead>
                        <tr>
                            <th scope="col" className="br2-cb"><span className="v2-sr">Expand</span></th>
                            {head('Variant / Category', null, false)}
                            {head('Stock', 'Active units currently in stock')}
                            {head('Avg Days', 'Average days this category has been in stock')}
                            {head(criticalLabel(aging.criticalDays), criticalTip(aging.criticalDays))}
                            {head('Sold YoY', `Year-over-year % change in sold units — Q${q} ${yr} vs Q${q} ${yr - 1}`)}
                            {head('YoY %', 'Year-over-year % change in sold units')}
                            {head('Sold QoQ', `Units sold Q${q} ${yr} vs Q${prevQ} ${prevQYear}`)}
                            {head('QoQ %', 'Quarter-over-quarter % change in sold units')}
                        </tr>
                    </thead>
                    <tbody>
                        {variants.length === 0 && (
                            <tr><td colSpan={9}><p className="br2-empty-row">No variants match the current filter.</p></td></tr>
                        )}
                        {variants.map((v) => {
                            const subs = subsFor(v);
                            const hasSubs = subs.length > 0;
                            const isOpen = open.has(v.variant);
                            const a = aggregateVariantRow(v, subs);
                            return (
                                <React.Fragment key={v.variant}>
                                    <tr
                                        className={`br2-vr${hasSubs ? ' br2-tr' : ''}`}
                                        data-variant={v.variant}
                                        onClick={(e) => { if (hasSubs && !e.target.closest('button')) toggle(v.variant); }}
                                    >
                                        <td className="br2-cb">
                                            {hasSubs && (
                                                <button
                                                    type="button"
                                                    className="br2-exp"
                                                    aria-expanded={isOpen}
                                                    aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${v.variant}`}
                                                    onClick={() => toggle(v.variant)}
                                                >
                                                    <span aria-hidden="true">{isOpen ? '▼' : '▶'}</span>
                                                </button>
                                            )}
                                        </td>
                                        <td><b className="br2-strong">{v.variant}</b></td>
                                        <td className="v2-r br2-num">{a.stock || '—'}</td>
                                        <td className="v2-r br2-num">{a.avgDays != null ? `${a.avgDays}d` : '—'}</td>
                                        <td className="v2-r">{a.critical > 0 ? <span className="v2-chip v2-chip--crit br2-crit">{a.critical}</span> : <span className="br2-ink-muted">—</span>}</td>
                                        <td className="v2-r br2-num">{a.tq} <span className="br2-ink-muted">vs {a.lyq}</span></td>
                                        <td className="v2-r"><Delta value={a.yoy} /></td>
                                        <td className="v2-r br2-num">{a.tq} <span className="br2-ink-muted">vs {a.lq}</span></td>
                                        <td className="v2-r"><Delta value={a.qoq} /></td>
                                    </tr>
                                    {isOpen && hasSubs && subs.map((s) => {
                                        const tone = severityTone(s.severity);
                                        const dTone = avgDaysTone(s.avg_days, aging);
                                        return (
                                            <tr key={s.category} className="br2-sub-row" data-category={s.category}>
                                                <td className="br2-cb">
                                                    <span className={`br2-sev v2-tone-${tone}`} data-testid="severity" data-tone={tone} title={SEVERITY_TEXT[tone]} />
                                                    <span className="v2-sr">{SEVERITY_TEXT[tone]}</span>
                                                </td>
                                                <td>
                                                    {s.category}
                                                    {s.mf_category && <span className="br2-mf">{s.mf_category}</span>}
                                                </td>
                                                <td className="v2-r br2-num">{s.in_stock || '—'}</td>
                                                <td className="v2-r br2-num">
                                                    {s.avg_days != null
                                                        ? <span className={dTone ? `br2-ink-${dTone} br2-strong` : undefined}>{s.avg_days}d</span>
                                                        : '—'}
                                                </td>
                                                <td className="v2-r">{s.critical > 0 ? <span className="v2-chip v2-chip--crit br2-crit">{s.critical}</span> : <span className="br2-ink-muted">—</span>}</td>
                                                <td className="v2-r br2-num">{s.tq_sold} <span className="br2-ink-muted">vs {s.lyq_sold ?? '—'}</span></td>
                                                <td className="v2-r"><Delta value={s.yoy_pct} /></td>
                                                <td className="v2-r br2-num">{s.tq_sold} <span className="br2-ink-muted">vs {s.lq_sold}</span></td>
                                                <td className="v2-r"><Delta value={s.qoq_pct} /></td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

CategoryRisk.propTypes = { trendsData: PropTypes.object.isRequired };

/** The Market sub-view: states, market signals, risk figures, category risk. */
export default function MarketViewV2({
    trendsData, loading, error, onRetry,
}) {
    if (loading) return <LoadingState label="Loading category trends..." />;
    if (error) return <ErrorState message={error} onRetry={onRetry} />;
    if (!trendsData) return <EmptyState message="No data loaded yet." />;
    return (
        <div className="br2-panel">
            <MarketSignals trendsData={trendsData} />
            <RiskKpis variants={trendsData.variant_trends || []} />
            <CategoryRisk trendsData={trendsData} />
        </div>
    );
}

MarketViewV2.propTypes = {
    trendsData: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRetry: PropTypes.func,
};
